import {useEffect, useState} from 'react'
import {
  StyledTextField,
  StyledInfoIcon,
  StyledButton,
  StyledErrorMessage,
  StyledSpan,
  StyledMenuItem,
  StyledAlert,
  StyledMenu,
  StyledActionButton
} from './UserFeedbackDashboardStyle'
import {InputAdornment, Tooltip, IconButton, Divider, Snackbar, CircularProgress} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import EditIcon from '@mui/icons-material/Edit'
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined'
import UserFeedbackDialog from './UserfeedbackDialog'
import CreateClosePopup from './CreateClosePopup'
import {useUserFeedback} from '../../../store/hooks/userFeedback'
import ParticipantConsentDialog from './ParticipantConsentDialog'

const UserFeedbackDashboard = () => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [popupConfig, setPopupConfig] = useState({})
  const [showConsentDialog, setShowConsentDialog] = useState(false)

  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState(false)
  const [isEventCreated, setIsEventCreated] = useState(false)
  const [jobTitle, setJobTitle] = useState('')
  const [buttonText, setButtonText] = useState('Start')
  const [isEditingEvent, setIsEditingEvent] = useState(false)

  // Persona/Archetype options for dropdown
  const personaArchetypeOptions = [
    'Community Champion Chris',
    'Decision Maker Devin',
    'Efficiency Emi',
    'Techie Tamar',
    'Data Wrangler Dani',
    'Investigator Ivy',
    'Storyteller Samir',
    'Problem Solver Paula'
  ]

  // Generate UUID for participant session
  const generateUUID = () => {
    const crypto = globalThis.crypto || globalThis.msCrypto
    return crypto.randomUUID()
  }

  const {
    eventDetails,
    isRecording,
    updateEventDetailsMutation,
    updateEventDetailsLoading,
    setEventDetails,
    setIsRecording,
    isUserFeedbackAgreed,
    setIsUserFeedbackAgreed
  } = useUserFeedback()

  useEffect(() => {
    if (sessionStorage.getItem('eventName')) {
      setInputValue(sessionStorage.getItem('eventName'))
      setJobTitle(sessionStorage.getItem('eventParticipantJob'))
      setIsRecording(true)
      setButtonText('End')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (eventDetails?.EVENT_NAME) {
      setInputValue(eventDetails.EVENT_NAME)
      setIsEventCreated(true)
    } else {
      setInputValue('')
      setIsEventCreated(false)
    }
  }, [eventDetails])

  const checkEventExists = (eventName) => {
    if (eventDetails?.EVENT_NAME) {
      return eventDetails.EVENT_NAME.toLowerCase() === eventName.toLowerCase()
    }
    return false
  }

  const resetEvent = () => {
    setIsEventCreated(false)
    setIsEditingEvent(false)
    setInputValue('')
    setJobTitle('')
    setError(false)

    if (eventDetails) {
      setEventDetails({
        ...eventDetails,
        EVENT_ID: null,
        EVENT_NAME: null,
        USER_EMAIL: null,
        HAS_RECORDING: null,
        IS_ACTIVE: null
      })
    }
  }

  const handleInputChange = (event) => {
    setInputValue(event.target.value)
    setError(false)
  }

  const handleCreateEvent = async () => {
    const trimmedEventName = inputValue.trim()

    if (checkEventExists(trimmedEventName)) {
      setError(true)
      return
    }

    setError(false)

    try {
      const createdEvent = await updateEventDetailsMutation({
        variables: {
          eventId: null,
          eventName: trimmedEventName,
          isActive: true,
          hasRecording: false
        }
      })
      setEventDetails({
        ...eventDetails,
        ...createdEvent?.data?.updateEvent.updateEvent
      })
      setIsEventCreated(true)
    } catch (mutationError) {
      console.error('Error creating event:', mutationError)
      setError(true)
    }
  }
  const handleRecord = async () => {
    // Generate UUID for participant session
    const sessionUUID = generateUUID()
    setIsRecording(true)
    setButtonText('End')
    sessionStorage.setItem('eventName', eventDetails.EVENT_NAME)
    sessionStorage.setItem('eventParticipantName', sessionUUID)
    sessionStorage.setItem('eventParticipantJob', jobTitle)
    if (!eventDetails?.HAS_RECORDING) {
      try {
        const updateEvent = await updateEventDetailsMutation({
          variables: {
            eventId: eventDetails.EVENT_ID,
            eventName: eventDetails.EVENT_NAME,
            isActive: eventDetails.IS_ACTIVE,
            hasRecording: true
          }
        })

        setEventDetails({
          ...eventDetails,
          ...updateEvent.data.updateEvent.updateEvent
        })
      } catch (mutationError) {
        console.error('Error updating has recordings', mutationError)
      }
    }
  }

  const handleEndRecording = () => {
    setIsRecording(false)

    sessionStorage.removeItem('eventName')
    sessionStorage.removeItem('eventParticipantName')
    sessionStorage.removeItem('eventParticipantJob')

    setButtonText('Start')
    setJobTitle('')
    setIsUserFeedbackAgreed(false)
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = async () => {
    setAnchorEl(null)
  }

  const handleEndSessionClick = () => {
    if (sessionStorage.getItem('doNotShowEndAndSavePopup')) {
      handleEndRecording()
    } else setIsDialogOpen(true)
  }

  const handleEndEvent = async () => {
    try {
      await updateEventDetailsMutation({
        variables: {
          eventId: eventDetails.EVENT_ID,
          eventName: eventDetails.EVENT_NAME,
          isActive: false,
          hasRecording: eventDetails.HAS_RECORDING
        }
      })

      if (isRecording) {
        handleEndRecording()
      }
      resetEvent()
    } catch (mutationError) {
      console.error('Error ending the event', mutationError)
    }
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
  }

  const handleEndAndSave = () => {
    if (doNotShowAgain) sessionStorage.setItem('doNotShowEndAndSavePopup', '1')
    setIsDialogOpen(false)
    setIsSnackbarOpen(true)
    handleEndRecording()
  }

  const handleSnackbarClose = () => {
    setIsSnackbarOpen(false)
  }

  const handleCreateNewEvent = () => {
    setPopupConfig({
      title: 'Create New Event!',
      message: (
        <>
          Are you sure you want to create a new event? <br />
          This action will close the current event.
        </>
      ),
      confirmButtonText: 'Confirm',
      confirmButtonColor: 'primary',
      showWarningIcon: false,
      onConfirm: () => {
        setIsPopupOpen(false)
        handleMenuClose()
        handleEndEvent()
      }
    })
    setIsPopupOpen(true)
  }

  const handleEditEventTitle = () => {
    if (eventDetails?.HAS_RECORDING) {
      return
    }
    setIsEventCreated(false)
    setInputValue(eventDetails.EVENT_NAME)
    setIsEditingEvent(true)
    handleMenuClose()
  }

  const handleUpdateEvent = async () => {
    const trimmedEventName = inputValue.trim()

    if (checkEventExists(trimmedEventName) && trimmedEventName !== eventDetails.EVENT_NAME) {
      setError(true)
      return
    }

    setError(false)

    try {
      const updateEvent = await updateEventDetailsMutation({
        variables: {
          eventId: eventDetails.EVENT_ID,
          eventName: trimmedEventName,
          isActive: eventDetails.IS_ACTIVE,
          hasRecording: eventDetails.HAS_RECORDING
        }
      })
      setEventDetails({
        ...eventDetails,
        ...updateEvent.data.updateEvent.updateEvent
      })

      setIsEventCreated(true)
      setIsEditingEvent(false)
    } catch (mutationError) {
      console.error('Error updating event:', mutationError)
      setError(true)
    }
  }

  const handleCloseEvent = () => {
    setPopupConfig({
      title: 'Close Current Event!',
      message: 'Are you sure you want to close the current event?',
      confirmButtonText: 'Close Event',
      confirmButtonColor: 'error',
      showWarningIcon: true,
      onConfirm: () => {
        setIsPopupOpen(false)
        handleMenuClose()
        handleEndEvent()
      }
    })
    setIsPopupOpen(true)
  }

  // Helper function for button content
  const getButtonContent = (text, isLoading) =>
    isLoading ? (
      <>
        {text} <CircularProgress size={16} sx={{ml: 1, color: 'inherit'}} />
      </>
    ) : (
      text
    )

  // Determine which button to show for event creation/update
  const renderEventButton = () => {
    const isDisabled = !inputValue.trim() || updateEventDetailsLoading
    const action = isEditingEvent ? handleUpdateEvent : handleCreateEvent
    const text = isEditingEvent ? 'Update Event' : 'Create Event'

    return (
      <StyledButton onClick={action} disabled={isDisabled}>
        {getButtonContent(text, updateEventDetailsLoading)}
      </StyledButton>
    )
  }

  return (
    <>
      {isEventCreated ? (
        <>
          <StyledSpan className='event-title'>Event title:</StyledSpan>
          <StyledTextField variant='outlined' value={inputValue} disabled />
          {isUserFeedbackAgreed ? (
            <>
              <StyledSpan className='participant'>Participant Details:</StyledSpan>
              <StyledTextField
                select
                variant='outlined'
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                disabled={!isUserFeedbackAgreed || isRecording}
                displayEmpty
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return <span style={{color: '#999'}}>Persona/Archetype</span>
                    }
                    return selected
                  }
                }}
              >
                {personaArchetypeOptions.map((title) => (
                  <StyledMenuItem key={title} value={title}>
                    {title}
                  </StyledMenuItem>
                ))}
              </StyledTextField>
              <StyledButton
                onClick={isRecording ? handleEndSessionClick : handleRecord}
                disabled={!jobTitle || !isUserFeedbackAgreed}
                className={isRecording ? 'end' : 'record'}
              >
                {buttonText}
              </StyledButton>
            </>
          ) : (
            <StyledActionButton onClick={() => setShowConsentDialog(true)} variant='contained' color='primary'>
              Add Participant
            </StyledActionButton>
          )}

          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
          <StyledMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <StyledMenuItem onClick={handleCreateNewEvent} className='create-new-event'>
              <AddIcon />
              Create new event
            </StyledMenuItem>
            <Divider />
            <Tooltip title={isRecording ? 'Event title can be edited only before starting the recording' : ''}>
              <StyledMenuItem
                onClick={handleEditEventTitle}
                className='edit-event-title'
                disabled={eventDetails.HAS_RECORDING}
              >
                <EditIcon />
                Edit event title
              </StyledMenuItem>
            </Tooltip>
            <Divider />
            <StyledMenuItem onClick={handleCloseEvent} className='close-event'>
              <HighlightOffOutlinedIcon />
              Close event
            </StyledMenuItem>
          </StyledMenu>
          <ParticipantConsentDialog
            open={showConsentDialog}
            onClose={() => {
              setShowConsentDialog(false)
            }}
            onConfirm={() => {
              setShowConsentDialog(false)
              setIsUserFeedbackAgreed(true)
            }}
          />
          <UserFeedbackDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleEndAndSave}
            doNotShowAgain={doNotShowAgain}
            setDoNotShowAgain={setDoNotShowAgain}
          />
          <Snackbar
            open={isSnackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            anchorOrigin={{vertical: 'top', horizontal: 'center'}}
          >
            <StyledAlert icon={<CheckIcon fontSize='inherit' />} onClose={handleSnackbarClose}>
              Your recording has saved successfully!
            </StyledAlert>
          </Snackbar>
          <CreateClosePopup open={isPopupOpen} onClose={() => setIsPopupOpen(false)} {...popupConfig} />
        </>
      ) : (
        <>
          {error && <StyledErrorMessage>Event name already exists!</StyledErrorMessage>}
          <StyledTextField
            variant='outlined'
            placeholder='Event name'
            value={inputValue}
            onChange={handleInputChange}
            error={error}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <Tooltip title='Please provide a unique name for each event. This will act as a identifier to capture session outcomes.'>
                    <StyledInfoIcon />
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
          {renderEventButton()}
        </>
      )}
    </>
  )
}

export default UserFeedbackDashboard
